import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ArrowUp } from "lucide-react";

/**
 * Scroll-to-top button.
 * - Fades in after the visitor has scrolled past 400px.
 * - Hidden on admin routes so it doesn't compete with admin UI.
 * - Smooth-scrolls to the top of the document.
 * - Positioned bottom-right, above the mobile call FAB on smaller screens
 *   (the FAB is at bottom-5/right-5; this sits at bottom-20 on mobile and
 *   bottom-6 on desktop where the FAB is hidden).
 */
export default function ScrollToTopButton() {
    const location = useLocation();
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const onScroll = () => setVisible(window.scrollY > 400);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    if (location.pathname.startsWith("/admin")) return null;

    const handleClick = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    return (
        <button
            type="button"
            data-testid="scroll-to-top"
            onClick={handleClick}
            aria-label="Scroll to top of page"
            className={`fixed bottom-20 right-5 z-40 flex h-11 w-11 items-center justify-center rounded-full border border-brand-gold/60 bg-[#0b0e13] text-brand-gold2 shadow-[0_18px_36px_-12px_rgba(0,0,0,0.65)] transition-all duration-300 ease-out hover:bg-brand-gold hover:text-brand-bg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 focus-visible:ring-offset-brand-bg active:scale-95 md:bottom-6 ${
                visible
                    ? "translate-y-0 opacity-100"
                    : "pointer-events-none translate-y-3 opacity-0"
            }`}
        >
            <ArrowUp aria-hidden="true" className="h-4 w-4" />
        </button>
    );
}
